import { toast } from "sonner";

/**
 * Error handling utilities for consistent error management across the application.
 * Provides retry logic, error classification, user-friendly messaging, and toast notifications.
 */

/**
 * Configuration options for retry behavior.
 * 
 * @interface RetryOptions
 * @property {number} [maxAttempts=3] - Maximum number of retry attempts
 * @property {number} [delayMs=1000] - Base delay between retries in milliseconds
 * @property {boolean} [backoff=true] - Use exponential backoff (delay doubles each attempt)
 * @property {Function} [onRetry] - Callback invoked before each retry with attempt number
 * @property {Function} [shouldRetry] - Predicate to determine if error should be retried
 */
export interface RetryOptions {
  maxAttempts?: number;
  delayMs?: number;
  backoff?: boolean;
  onRetry?: (attempt: number) => void;
  shouldRetry?: (error: Error) => boolean;
}

/**
 * Retry a failed operation with exponential backoff strategy.
 * Automatically retries network errors, timeouts, and transient failures.
 * Respects shouldRetry predicate for custom retry logic.
 * 
 * @template T - Return type of the operation
 * @param {() => Promise<T>} operation - Async function to retry
 * @param {RetryOptions} [options={}] - Retry configuration
 * @returns {Promise<T>} Promise resolving to operation result
 * @throws {Error} Throws last error if all retries fail
 * 
 * @example
 * // Retry API call with default settings (3 attempts, exponential backoff)
 * const data = await withRetry(
 *   () => fetch('/api/data').then(r => r.json()),
 *   {
 *     onRetry: (attempt) => console.log(`Retry attempt ${attempt}`),
 *     shouldRetry: (error) => !error.message.includes('404')
 *   }
 * );
 * 
 * @example
 * // Custom retry settings for flaky websocket
 * const connection = await withRetry(
 *   () => connectWebSocket(),
 *   {
 *     maxAttempts: 5,
 *     delayMs: 2000,
 *     backoff: true, // 2s, 4s, 8s, 16s delays
 *     shouldRetry: (error) => isNetworkError(error)
 *   }
 * );
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    delayMs = 1000,
    backoff = true,
    onRetry,
    shouldRetry = () => true,
  } = options;

  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      // Check if we should retry this error
      if (!shouldRetry(lastError)) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === maxAttempts) {
        break;
      }

      // Calculate delay with optional backoff
      const delay = backoff ? delayMs * Math.pow(2, attempt - 1) : delayMs;

      // Notify about retry
      if (onRetry) {
        onRetry(attempt);
      }

      // Wait before next attempt
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Check if error is network-related (connection, timeout, fetch failures).
 * Used to determine if operation should be retried.
 * 
 * @param {Error} error - Error to check
 * @returns {boolean} True if error is network-related
 * 
 * @example
 * try {
 *   await fetchData();
 * } catch (error) {
 *   if (isNetworkError(error)) {
 *     // Show "Check your connection" message
 *   }
 * }
 */
export function isNetworkError(error: Error): boolean {
  const networkErrorMessages = [
    "network",
    "fetch",
    "timeout",
    "connection",
    "offline",
    "ERR_NETWORK",
    "Failed to fetch",
  ];

  return networkErrorMessages.some((msg) =>
    error.message.toLowerCase().includes(msg.toLowerCase())
  );
}

/**
 * Check if error is authentication-related (401, unauthorized, token expired).
 * Used to trigger re-authentication flows.
 * 
 * @param {Error} error - Error to check
 * @returns {boolean} True if error requires authentication
 * 
 * @example
 * if (isAuthError(error)) {
 *   redirectToLogin();
 * }
 */
export function isAuthError(error: Error): boolean {
  const authErrorMessages = ["401", "unauthorized", "unauthenticated", "token"];
  return authErrorMessages.some((msg) =>
    error.message.toLowerCase().includes(msg.toLowerCase())
  );
}

/**
 * Check if error is rate limit related (429 status, rate limit exceeded).
 * Used to trigger cooldown UI and retry logic.
 * 
 * @param {Error} error - Error to check
 * @returns {boolean} True if error is due to rate limiting
 * 
 * @example
 * if (isRateLimitError(error)) {
 *   showCooldownTimer(60); // Show 60s countdown
 * }
 */
export function isRateLimitError(error: Error): boolean {
  return error.message.includes("429") || error.message.toLowerCase().includes("rate limit");
}

/**
 * Convert technical error messages into user-friendly descriptions.
 * Classifies errors by type (network, auth, rate limit) and returns
 * appropriate human-readable messages.
 * 
 * @param {Error} error - Error to format
 * @returns {string} User-friendly error message
 * 
 * @example
 * const message = getUserFriendlyErrorMessage(error);
 * toast.error(message); // Shows "Network error. Please check your connection."
 */
export function getUserFriendlyErrorMessage(error: Error): string {
  if (isNetworkError(error)) {
    return "Network error. Please check your connection and try again.";
  }

  if (isAuthError(error)) {
    return "Authentication error. Please sign in again.";
  }

  if (isRateLimitError(error)) {
    return "Too many requests. Please wait a moment and try again.";
  }

  // Return original message if no specific type matched
  return error.message || "An unexpected error occurred";
}

/**
 * Handle error with appropriate toast notification based on error type.
 * Automatically classifies error and shows contextual actions (Retry, Sign In).
 * Always logs error to console for debugging.
 * 
 * @param {Error} error - Error to handle
 * @param {string} [customMessage] - Optional custom message to override default
 * 
 * @example
 * // Basic error handling
 * try {
 *   await submitForm();
 * } catch (error) {
 *   handleError(error); // Shows toast with Retry button for network errors
 * }
 * 
 * @example
 * // Custom message
 * handleError(error, 'Failed to save document. Please try again.');
 */
export function handleError(error: Error, customMessage?: string): void {
  const message = customMessage || getUserFriendlyErrorMessage(error);

  if (isNetworkError(error)) {
    toast.error("Network Error", {
      description: message,
      action: {
        label: "Retry",
        onClick: () => window.location.reload(),
      },
    });
  } else if (isAuthError(error)) {
    toast.error("Authentication Required", {
      description: message,
      action: {
        label: "Sign In",
        onClick: () => window.location.href = "/",
      },
    });
  } else if (isRateLimitError(error)) {
    toast.warning("Rate Limit Reached", {
      description: message,
    });
  } else {
    toast.error("Error", {
      description: message,
    });
  }

  // Always log to console for debugging
  console.error("Error occurred:", error);
}

/**
 * Wrap an async operation with automatic error handling, retry logic, and toast notifications.
 * Returns null on failure instead of throwing, making it safe for use in React components.
 * Displays retry progress toasts and invokes success/error callbacks.
 * 
 * @template T - Return type of the operation
 * @param {() => Promise<T>} operation - Async function to execute safely
 * @param {object} [options={}] - Configuration options
 * @param {string} [options.errorMessage] - Custom error message for toast
 * @param {RetryOptions} [options.retry] - Retry configuration (enables retries if provided)
 * @param {Function} [options.onSuccess] - Callback invoked with result on success
 * @param {Function} [options.onError] - Callback invoked with error on failure
 * @returns {Promise<T | null>} Operation result or null on failure
 * 
 * @example
 * // Safe API call with retries
 * const data = await safeOperation(
 *   () => fetchUserData(userId),
 *   {
 *     errorMessage: 'Failed to load user profile',
 *     retry: { maxAttempts: 3, delayMs: 1000 },
 *     onSuccess: (user) => console.log('Loaded:', user.name),
 *     onError: (error) => logToSentry(error)
 *   }
 * );
 * 
 * if (data) {
 *   setUser(data); // Only runs if successful
 * }
 * 
 * @example
 * // Simple safe operation without retries
 * const saved = await safeOperation(
 *   () => saveDocument(doc),
 *   { errorMessage: 'Save failed' }
 * );
 * 
 * @example
 * // In React component with loading state
 * const handleSubmit = async () => {
 *   setLoading(true);
 *   const result = await safeOperation(
 *     () => submitForm(formData),
 *     {
 *       retry: { maxAttempts: 2 },
 *       onSuccess: () => navigate('/success'),
 *       onError: () => setLoading(false)
 *     }
 *   );
 *   setLoading(false);
 * };
 */
export async function safeOperation<T>(
  operation: () => Promise<T>,
  options: {
    errorMessage?: string;
    retry?: RetryOptions;
    onSuccess?: (result: T) => void;
    onError?: (error: Error) => void;
  } = {}
): Promise<T | null> {
  try {
    let result: T;

    if (options.retry) {
      result = await withRetry(operation, {
        ...options.retry,
        onRetry: (attempt) => {
          toast.info(`Retrying... (Attempt ${attempt})`, {
            duration: 2000,
          });
          options.retry?.onRetry?.(attempt);
        },
      });
    } else {
      result = await operation();
    }

    if (options.onSuccess) {
      options.onSuccess(result);
    }

    return result;
  } catch (error) {
    const err = error as Error;
    handleError(err, options.errorMessage);

    if (options.onError) {
      options.onError(err);
    }

    return null;
  }
}
